import axiosInstance from "@/helpers/axiosInstance";
import {
    COMMENT_LIKE_TARGET_TYPE,
    PLAYABLE_PROPERTY_LIKE_TARGET_TYPE,
} from "@/constants/like.constants";

export interface LikeResponse {
    message: string;
}

export type LikeTargetType = typeof PLAYABLE_PROPERTY_LIKE_TARGET_TYPE | typeof COMMENT_LIKE_TARGET_TYPE;

export interface LikeCountResult {
    targetType: LikeTargetType;
    targetId: string;
    count: number;
    likedByCurrentUser: boolean;
}

export interface LikeCountsResponse {
    counts: LikeCountResult[];
}

export interface LikeCountRequestItem {
    targetType: LikeTargetType;
    targetId: string;
}

/**
 * Creates a like for a playable property (idempotent).
 */
export async function likePlayableProperty(
    playablePropertyId: string,
    userId: string,
): Promise<LikeResponse> {
    const response = await axiosInstance.post<LikeResponse>(
        `/api/likes/playable-properties/${playablePropertyId}`,
        { userId },
    );
    return response.data;
}

/**
 * Removes a like for a playable property.
 */
export async function unlikePlayableProperty(
    playablePropertyId: string,
    userId: string,
): Promise<LikeResponse> {
    const response = await axiosInstance.delete<LikeResponse>(
        `/api/likes/playable-properties/${playablePropertyId}`,
        { data: { userId } },
    );
    return response.data;
}

/**
 * Creates a like for a comment (idempotent).
 */
export async function likeComment(commentId: string, userId: string): Promise<LikeResponse> {
    const response = await axiosInstance.post<LikeResponse>(`/api/likes/comments/${commentId}`, { userId });
    return response.data;
}

/**
 * Removes a like for a comment.
 */
export async function unlikeComment(commentId: string, userId: string): Promise<LikeResponse> {
    const response = await axiosInstance.delete<LikeResponse>(`/api/likes/comments/${commentId}`, {
        data: { userId },
    });
    return response.data;
}

/**
 * Batch like counts for playable properties and comments.
 */
export async function getLikeCounts(params: {
    items: LikeCountRequestItem[];
    userId?: string;
}): Promise<LikeCountsResponse> {
    const response = await axiosInstance.post<LikeCountsResponse>("/api/likes/counts", params);
    return response.data;
}

export interface LikerUser {
    id: string;
    nickname: string;
    profilePhotoUrl?: string | null;
}

export interface LikersResponse {
    data: LikerUser[];
    page: number;
    totalPages: number;
    hasNextPage: boolean;
    totalCount: number;
}

export interface GetLikersParams {
    targetType: LikeTargetType;
    targetId: string;
    page?: number;
    limit?: number;
}

export const LIKERS_DEFAULT_PAGE_SIZE = 10;

function resolveEffectivePageLimit(payload: Record<string, unknown>, requestedLimit: number): number {
    const payloadLimit = typeof payload.limit === "number" ? payload.limit : requestedLimit;
    return Math.max(1, payloadLimit);
}

function normalizeLikerUser(raw: unknown, index: number): LikerUser | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const record = raw as Record<string, unknown>;
    if (record.user && typeof record.user === "object") {
        return normalizeLikerUser(record.user, index);
    }
    const nicknameValue = record.nickname ?? record.displayName ?? record.emailAddress;
    const nickname = typeof nicknameValue === "string" ? nicknameValue.trim() : "";
    if (!nickname) {
        return null;
    }
    const idValue = record.id ?? record.userId;
    const id = typeof idValue === "string" || typeof idValue === "number" ? String(idValue) : `liker-${nickname}-${index}`;
    const photoValue = record.profilePhotoUrl ?? record.profile_photo_url ?? record.img;
    const profilePhotoUrl = typeof photoValue === "string" ? photoValue : null;
    return { id, nickname, profilePhotoUrl };
}

function normalizeLikersResponse(body: unknown, page: number, limit: number): LikersResponse {
    if (!body || typeof body !== "object") {
        throw new Error("Invalid likers response");
    }
    const payload = body as Record<string, unknown>;
    const items = payload.data;
    if (!Array.isArray(items)) {
        throw new Error("Invalid likers response: missing data array");
    }
    const currentPage = typeof payload.currentPage === "number" ? payload.currentPage : page;
    const effectiveLimit = resolveEffectivePageLimit(payload, limit);
    const pageOffset = Math.max(0, currentPage - 1) * effectiveLimit;
    const data = items
        .map((item, index) => normalizeLikerUser(item, pageOffset + index))
        .filter((item): item is LikerUser => item !== null);
    const totalCount = typeof payload.total === "number" ? payload.total : data.length;
    const totalPages = typeof payload.totalPages === "number"
        ? payload.totalPages
        : Math.max(1, Math.ceil(totalCount / effectiveLimit));
    const hasNextPage = typeof payload.hasNextPage === "boolean"
        ? payload.hasNextPage
        : currentPage < totalPages;
    return {
        data,
        page: currentPage,
        totalPages,
        hasNextPage,
        totalCount,
    };
}

/**
 * Returns a paginated list of users who liked the given entity.
 */
export async function getLikers({
    targetType,
    targetId,
    page = 1,
    limit = LIKERS_DEFAULT_PAGE_SIZE,
}: GetLikersParams): Promise<LikersResponse> {
    const response = await axiosInstance.get("/api/likes/users", {
        params: { targetType, targetId, page, limit },
    });
    return normalizeLikersResponse(response.data, page, limit);
}
